import React from 'react';
import { useTranslation } from 'react-i18next';

const val = (obj, key, fallback = '') => {
  const v = obj?.[key];
  if (v === null || v === undefined) return fallback;
  return String(v);
};

const cellText = (obj, key, fallback = '-') => {
  const s = val(obj, key, '').trim();
  return s ? s : fallback;
};

/** 동의 한 줄 — 라벨 + 값 텍스트만 */
function consentPersonalLine(data, label, key) {
  const v = data?.[key];
  if (v == null || v === '') return `${label}    -`;
  if (v === true || v === 1) return `${label}    동의`;
  if (v === false || v === 0) return `${label}    미동의`;
  const su = String(v).trim().toUpperCase();
  if (su === 'Y' || su === 'YES' || su === 'TRUE') return `${label}    동의`;
  if (su === 'N' || su === 'NO' || su === 'FALSE') return `${label}    미동의`;
  const t = String(v).trim();
  return `${label}    ${t}`;
}

const formatKoreanDate = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[1]}. ${m[2]}. ${m[3]}.`;
  return s;
};

export default function PersonalSignupPreview({
  signatureDataUrl,
  structureJson,
  dataJson,
}) {
  const { t } = useTranslation();
  const authorLabel = t('popup.previewPopup.author');
  const signatureLabel = t('popup.previewPopup.signature');
  const signatureAlt = t('popup.previewPopup.signatureAlt');
  const layoutRows = dataJson?.layoutRows || {};
  const row0 = (Array.isArray(layoutRows?.[0]) ? layoutRows[0][0] : null) || {};   // 개인정보
  const row1 = (Array.isArray(layoutRows?.[1]) ? layoutRows[1][0] : null) || {};   // 학력·소속정보
  const row2 = (Array.isArray(layoutRows?.[2]) ? layoutRows[2][0] : null) || {};   // 전문분야·역량
  const internRows = Array.isArray(layoutRows?.[3]) ? layoutRows[3] : [{}];
  const projectRows = Array.isArray(layoutRows?.[4]) ? layoutRows[4] : [{}];
  const row5 = (Array.isArray(layoutRows?.[5]) ? layoutRows[5][0] : null) || {};   // 매칭 선호도
  const row6 = (Array.isArray(layoutRows?.[6]) ? layoutRows[6][0] : null) || {};   // 추가 항목
  const snsRows = Array.isArray(layoutRows?.[7]) ? layoutRows[7] : [];
  const dividerRow0 = (Array.isArray(layoutRows?.[8]) ? layoutRows[8][0] : null) || {};

  const profilePhoto = String(row0?.profile_photo || '').trim();
  const joinDate = formatKoreanDate(val(dividerRow0, 'join_date', ''));

  const consentLabelByKey = {
    privacy_agreement_1: '개인정보의 제공 및 활용 동의',
    privacy_agreement_2: '고유식별정보의 제공 및 활용 동의',
    privacy_agreement_3: '개인정보의 제3자 제공동의',
  };

  const consentLines = [
    { key: 'privacy_agreement_1', label: consentLabelByKey.privacy_agreement_1 },
    { key: 'privacy_agreement_2', label: consentLabelByKey.privacy_agreement_2 },
    { key: 'privacy_agreement_3', label: consentLabelByKey.privacy_agreement_3 },
  ];

  // 경험·경력(카테고리 rowspan)
  const internCount = Math.max(1, internRows.length);
  const projectCount = Math.max(1, projectRows.length);
  const careerRowSpan = (1 + internCount) + (1 + projectCount); // header + rows, header + rows

  return (
    <div className="poc-preview-shell dfb-preview-shell personal-preview-shell">
      <div className="poc-preview-page dfb-preview-page personal-preview-page">
        <div className="poc-preview personal-preview">
          <div className="poc-title">개인 회원가입 신청서</div>
          <div className="poc-title-line"></div>

          <div className="personal-desc">
            GLOW Platform의 설립목적과 취지에 찬동하여 개인회원으로 가입하고 회원으로서의 제의무를 성실히 수행하고자 하오니
            가입을 승낙하여 주시기 바랍니다.
          </div>
          <div className="personal-dotted"></div>

          {/* 1) 개인정보(프로필사진 포함) ~ 7) 개인정보 동의: 단일 테이블 */}
          <table className="poc-table personal-table">
            <colgroup>
              <col style={{ width: '16%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
            </colgroup>
            <tbody>
              <tr>
                <td className="poc-cat personal-photo-cell" rowSpan={4}>
                  {profilePhoto ? (
                    <img className="personal-photo-img" src={profilePhoto} alt="profile" />
                  ) : (
                    <div className="personal-photo-placeholder" aria-hidden="true" />
                  )}
                </td>
                <td className="poc-label">성명</td>
                <td className="poc-value" colSpan={2}>{cellText(row0, 'full_name')}</td>
                <td className="poc-label">소속</td>
                <td className="poc-value" colSpan={2}>{cellText(row0, 'affiliation')}</td>
              </tr>
              <tr>
                <td className="poc-label">생년월일(나이)</td>
                <td className="poc-value" colSpan={2}>{cellText(row0, 'birth_date')}</td>
                <td className="poc-label">성별</td>
                <td className="poc-value" colSpan={2}>{cellText(row0, 'gender')}</td>
              </tr>
              <tr>
                <td className="poc-label">휴대폰 연락처</td>
                <td className="poc-value" colSpan={5}>{cellText(row0, 'mobile_phone')}</td>
              </tr>
              <tr>
                <td className="poc-label">이메일</td>
                <td className="poc-value" colSpan={5}>{cellText(row0, 'email')}</td>
              </tr>
              <tr>
                <td className="poc-cat" rowSpan={3}>학력<br />·<br />소속정보</td>
                <td className="poc-label">학교명</td>
                <td className="poc-value" colSpan={5}>{cellText(row1, 'school_name')}</td>
              </tr>
              <tr>
                <td className="poc-label">전공</td>
                <td className="poc-value">{cellText(row1, 'major')}</td>
                <td className="poc-label">학과</td>
                <td className="poc-value">{cellText(row1, 'department')}</td>
                <td className="poc-label">학위 과정</td>
                <td className="poc-value">{cellText(row1, 'degree_course')}</td>
              </tr>
              <tr>
                <td className="poc-label">입학연도</td>
                <td className="poc-value" colSpan={3}>{cellText(row1, 'entrance_year')}</td>
                <td className="poc-label">졸업(예정)연도</td>
                <td className="poc-value">{cellText(row1, 'graduation_year')}</td>
              </tr>
              <tr>
                <td className="poc-cat" rowSpan={3}>전문<br />분야·역량</td>
                <td className="poc-label">관심 분야</td>
                <td className="poc-value" colSpan={5} style={{ whiteSpace: 'pre-wrap' }}>{cellText(row2, 'interest_fields')}</td>
              </tr>
              <tr>
                <td className="poc-label">보유 기술 및 자격증</td>
                <td className="poc-value" colSpan={5} style={{ whiteSpace: 'pre-wrap' }}>{cellText(row2, 'skills_certifications')}</td>
              </tr>
              <tr>
                <td className="poc-label">주요 프로젝트·논문·성과</td>
                <td className="poc-value" colSpan={5} style={{ whiteSpace: 'pre-wrap' }}>{cellText(row2, 'projects_achievements')}</td>
              </tr>
              <tr>
                <td className="poc-cat" rowSpan={careerRowSpan}>경험·경력</td>
                <td className="poc-label" rowSpan={1 + internCount}>인턴·근무 경력</td>
                <td className="poc-label" colSpan={2}>기관명</td>
                <td className="poc-label">기간</td>
                <td className="poc-label" colSpan={2}>주요업무</td>
              </tr>
              {(internRows.length ? internRows : [{}]).map((r, idx) => (
                <tr key={`intern-${idx}`}>
                  <td className="poc-value" colSpan={2}>{cellText(r, 'intern_org', '')}</td>
                  <td className="poc-value">{cellText(r, 'intern_period', '')}</td>
                  <td className="poc-value" colSpan={2} style={{ whiteSpace: 'pre-wrap' }}>{cellText(r, 'intern_duty', '')}</td>
                </tr>
              ))}

              <tr>
                <td className="poc-label" rowSpan={1 + projectCount}>산학협력·연구 프로젝트<br />참여경력</td>
                <td className="poc-label" colSpan={2}>프로젝트명</td>
                <td className="poc-label">기간</td>
                <td className="poc-label" colSpan={2}>주요업무</td>
              </tr>
              {(projectRows.length ? projectRows : [{}]).map((r, idx) => (
                <tr key={`proj-${idx}`}>
                  <td className="poc-value" colSpan={2}>{cellText(r, 'project_name', '')}</td>
                  <td className="poc-value">{cellText(r, 'project_period', '')}</td>
                  <td className="poc-value" colSpan={2} style={{ whiteSpace: 'pre-wrap' }}>{cellText(r, 'project_duty', '')}</td>
                </tr>
              ))}
              <tr>
                <td className="poc-cat" rowSpan={4}>매칭<br />선호도</td>
                <td className="poc-label">희망 협력 분야</td>
                <td className="poc-value" colSpan={5} style={{ whiteSpace: 'pre-wrap' }}>{cellText(row5, 'desired_collab')}</td>
              </tr>
              <tr>
                <td className="poc-label">선호 기관 유형</td>
                <td className="poc-value" colSpan={5} style={{ whiteSpace: 'pre-wrap' }}>{cellText(row5, 'preferred_org_type')}</td>
              </tr>
              <tr>
                <td className="poc-label">근무·참여가능 지역</td>
                <td className="poc-value" colSpan={5} style={{ whiteSpace: 'pre-wrap' }}>{cellText(row5, 'preferred_region')}</td>
              </tr>
              <tr>
                <td className="poc-label">참여 가능 형태</td>
                <td className="poc-value" colSpan={5} style={{ whiteSpace: 'pre-wrap' }}>{cellText(row5, 'participation_type')}</td>
              </tr>
              <tr>
                <td className="poc-cat" rowSpan={2}>추가 항목</td>
                <td className="poc-label">자기소개<br />(500자 이내)</td>
                <td className="poc-value" colSpan={5} style={{ whiteSpace: 'pre-wrap' }}>{cellText(row6, 'self_intro')}</td>
              </tr>
              <tr>
                <td className="poc-label">SNS</td>
                <td className="poc-value" colSpan={5} style={{ whiteSpace: 'pre-wrap' }}>
                  {snsRows.length === 0
                    ? '-'
                    : snsRows.map((sr) => {
                        const typeLabel = sr?.snsType === 'other'
                          ? (sr?.snsTypeOther?.trim() || '기타')
                          : ({ facebook: 'Facebook', twitter: 'Twitter', instagram: 'Instagram', linkedin: 'LinkedIn', github: 'GitHub' }[sr?.snsType] || sr?.snsType || '');
                        const url = (sr?.sns || '').trim();
                        return typeLabel ? `${typeLabel}: ${url || '-'}` : (url || '-');
                      }).join('\n')}
                </td>
              </tr>
              <tr>
                <td colSpan={7} style={{ padding: 0 }}>
                  <div className="poc-consent-lines personal-consent">
                    {consentLines.map((it) => (
                      <div key={`personal-consent-${it.key}`} className="line">
                        {consentPersonalLine(dividerRow0, it.label, it.key)}
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div className="poc-footer">
            <div className="poc-footer-date">
              가입일 {joinDate}
            </div>

            <div className="poc-footer-row">
              <div className="poc-logo">
                <div className="mark">ⓘ</div>
                <div className="txt">
                  <div className="ko">한국외국어대학교 G-RISE 사업단</div>
                  <div className="en">HUFS G-Regional Innovation System & Education Center</div>
                </div>
              </div>

              <div className="poc-author">
                <span>{authorLabel}: {cellText(dividerRow0, 'writer_name', cellText(row0, 'full_name', '-'))}</span>
                <div className="poc-author-signature-overlap">
                  {signatureDataUrl && <img src={signatureDataUrl} alt={signatureAlt} />}
                  <span className="poc-author-signature-label">{signatureLabel}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

